import { CheckCheck, ShoppingCart, Info } from 'lucide-react'
import type { Notificacao } from '../../services/notificacaoService'
import { useNavigate } from 'react-router-dom'

interface NotificationPanelProps {
  notificacoes: Notificacao[]
  naoLidas: number
  onMarcarLida: (id: string) => void
  onMarcarTodasLidas: () => void
  onClose: () => void
}

export function NotificationPanel({
  notificacoes,
  naoLidas,
  onMarcarLida,
  onMarcarTodasLidas,
  onClose,
}: NotificationPanelProps) {
  const navigate = useNavigate()

  const getIcon = (tipo: Notificacao['tipo']) => {
    switch (tipo) {
      case 'pedido_criado':
      case 'pedido_atualizado':
      case 'status_alterado':
        return <ShoppingCart className="h-5 w-5 text-blue-500" />
      default:
        return <Info className="h-5 w-5 text-gray-500" />
    }
  }

  const handleNotificationClick = (notif: Notificacao) => {
    if (!notif.lida) {
      onMarcarLida(notif.id)
    }
    
    if (notif.pedidoId) {
      navigate(`/pedidos/editar/${notif.pedidoId}`)
      onClose()
    }
  }

  const formatDate = (date: string) => {
    try {
      const now = new Date()
      const notifDate = new Date(date)
      const diffMs = now.getTime() - notifDate.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'agora mesmo'
      if (diffMins < 60) return `há ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`
      if (diffHours < 24) return `há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`
      if (diffDays < 7) return `há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`
      
      return notifDate.toLocaleDateString('pt-BR')
    } catch {
      return 'Data inválida'
    }
  }

  return (
    <div className="fixed inset-x-4 top-20 mx-auto w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Notificações
          </h3>
          {naoLidas > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {naoLidas} {naoLidas === 1 ? 'nova' : 'novas'}
            </p>
          )}
        </div>
        {naoLidas > 0 && (
          <button
            onClick={onMarcarTodasLidas}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            <CheckCheck className="h-3 w-3" />
            Marcar todas como lidas
          </button>
        )}
      </div>

      {/* Lista de notificações */}
      <div className="max-h-96 overflow-y-auto">
        {notificacoes.length === 0 ? (
          <div className="p-8 text-center">
            <Info className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Nenhuma notificação
            </p>
          </div>
        ) : (
          notificacoes.map((notif) => (
            <button
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              className={`w-full border-b border-gray-100 p-4 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50 ${
                !notif.lida ? 'bg-blue-50 dark:bg-blue-900/10' : ''
              }`}
            >
              <div className="flex gap-3">
                <div className="flex-shrink-0">{getIcon(notif.tipo)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-medium ${
                      !notif.lida
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {notif.titulo}
                    </p>
                    {!notif.lida && (
                      <span className="flex-shrink-0 h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    {notif.mensagem}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {formatDate(notif.createdAt)}
                  </p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notificacoes.length > 0 && (
        <div className="border-t border-gray-200 p-2 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  )
}
